import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { Check, Security, Person, Email, Shield } from '@mui/icons-material';
import axios from 'axios';

// ─── Design Tokens (Nautilus Console — "Bridge Console") ─────────────────────
const tokens = {
    bg: '#0a1622',   // Abyssal
    surface: '#0f2134',   // Deep Ocean — card material
    surfaceHi: '#152840',   // Elevated surface (controls/inputs)
    border: '#2c3e50',   // Panel border
    borderSand: 'rgba(204, 186, 155, 0.30)', // Sand trace — accent border
    sand: '#ccba9b',   // Brand accent
    sandDim: 'rgba(204, 186, 155, 0.12)',
    textPrimary: '#ffffff',
    textSecondary: '#b0bec5',
    textMuted: '#546e7a',
    error: '#ef5350',
    errorDim: 'rgba(239, 83, 80, 0.10)',
};

// ─── Scope metadata ───────────────────────────────────────────────────────────
interface ScopeInfo { label: string; description: string; Icon: React.ElementType }
const scopeMeta: Record<string, ScopeInfo> = {
    openid: { label: 'OpenID', description: 'Verify your identity', Icon: Shield },
    profile: { label: 'Profile', description: 'Read your basic profile info', Icon: Person },
    email: { label: 'Email', description: 'Read your email address', Icon: Email },
};
const defaultScopeMeta: ScopeInfo = { label: '', description: '', Icon: Check };

// ─── Component ────────────────────────────────────────────────────────────────
const OAuthConsentPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [consentData, setConsentData] = useState<{
        client: { client_name: string };
        scopes: string[];
    } | null>(null);

    // ── Data fetching (unchanged) ──────────────────────────────────────────────
    useEffect(() => {
        const fetchConsentInfo = async () => {
            try {
                const headers: any = { 'Accept': 'application/json' };
                const token = localStorage.getItem('access_token');
                if (token) { headers['Authorization'] = `Bearer ${token}`; }

                const response = await axios.get(`/oauth/authorize${location.search}`, { headers });

                if (response.data.client) {
                    setConsentData(response.data);
                } else if (response.data.redirect_uri) {
                    window.location.href = response.data.redirect_uri;
                    return;
                } else {
                    console.log('No consent UI needed or unexpected response format:', response.data);
                }
                setLoading(false);
            } catch (err: any) {
                console.error('Failed to fetch consent info:', err);

                if (err.response?.status === 401) {
                    localStorage.removeItem('access_token');
                    localStorage.removeItem('user');
                    const returnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
                    window.location.href = `/login?returnUrl=${returnUrl}`;
                    return;
                }

                const responseData = err.response?.data;
                let errorMessage = 'Failed to initialize authorization.';
                if (responseData) {
                    if (responseData.error_description) errorMessage = responseData.error_description;
                    else if (responseData.error) errorMessage = responseData.error;
                    else if (typeof responseData === 'string') errorMessage = `Server Error: ${responseData.substring(0, 200)}...`;
                    else errorMessage = JSON.stringify(responseData);
                } else if (err.message) {
                    errorMessage = err.message;
                }

                setError(errorMessage);
                setLoading(false);
            }
        };

        fetchConsentInfo();
    }, [location.search]);

    // ── Consent handler (unchanged) ───────────────────────────────────────────
    const handleConsent = async (confirm: boolean) => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('confirm', confirm.toString());

            const response = await axios.post(`/oauth/authorize${location.search}`, formData, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                    'Accept': 'application/json',
                },
            });

            if (response.data.redirect_uri) {
                window.location.href = response.data.redirect_uri;
            } else if (typeof response.data === 'string' && response.data.startsWith('http')) {
                window.location.href = response.data;
            } else {
                console.log('Consent response:', response.data);
            }
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to process consent.');
            setLoading(false);
        }
    };

    // ── Loading state ─────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                minHeight: '100vh', background: tokens.bg,
            }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
                    <div style={{
                        width: 44, height: 44, borderRadius: 4,
                        background: tokens.sandDim,
                        border: `1px solid ${tokens.borderSand}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Security style={{ color: tokens.sand, fontSize: 20 }} />
                    </div>
                    <CircularProgress size={20} sx={{ color: tokens.sand }} />
                    <span style={{ color: tokens.textMuted, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                        Fetching Authorization
                    </span>
                </div>
            </div>
        );
    }

    // ── Error state ───────────────────────────────────────────────────────────
    if (error) {
        return (
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                minHeight: '100vh', background: tokens.bg, padding: 16,
            }}>
                <div style={{
                    maxWidth: 460, width: '100%',
                    background: tokens.surface,
                    border: `1px solid ${tokens.border}`,
                    borderRadius: 6,
                    overflow: 'hidden',
                }}>
                    {/* Header strip */}
                    <div style={{
                        padding: '12px 20px',
                        borderBottom: `1px solid ${tokens.border}`,
                        display: 'flex', alignItems: 'center', gap: 10,
                        background: tokens.surfaceHi,
                    }}>
                        <span style={{
                            fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
                            color: tokens.error, fontWeight: 600,
                        }}>
                            Authorization Error
                        </span>
                    </div>
                    <div style={{ padding: '20px 24px 24px' }}>
                        <div style={{
                            background: tokens.errorDim,
                            border: `1px solid rgba(239, 83, 80, 0.25)`,
                            borderRadius: 4, padding: '12px 14px',
                            color: tokens.error, fontSize: 13, lineHeight: 1.5,
                            marginBottom: 20,
                        }}>
                            {error}
                        </div>
                        <button
                            onClick={() => navigate('/')}
                            style={{
                                background: tokens.surfaceHi,
                                border: `1px solid ${tokens.border}`,
                                borderRadius: 4, padding: '8px 16px',
                                color: tokens.textSecondary, fontSize: 12,
                                letterSpacing: '0.06em', textTransform: 'uppercase',
                                cursor: 'pointer', fontWeight: 600,
                                transition: 'border-color 0.15s, color 0.15s',
                            }}
                            onMouseEnter={e => {
                                (e.currentTarget as HTMLButtonElement).style.borderColor = tokens.sand;
                                (e.currentTarget as HTMLButtonElement).style.color = tokens.sand;
                            }}
                            onMouseLeave={e => {
                                (e.currentTarget as HTMLButtonElement).style.borderColor = tokens.border;
                                (e.currentTarget as HTMLButtonElement).style.color = tokens.textSecondary;
                            }}
                        >
                            Back to Console
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!consentData) return null;

    // ── Main consent panel ────────────────────────────────────────────────────
    return (
        <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            minHeight: '100vh', background: tokens.bg, padding: 16,
            fontFamily: '"Inter", "Roboto", system-ui, sans-serif',
        }}>
            <div style={{
                maxWidth: 480, width: '100%',
                background: tokens.surface,
                border: `1px solid ${tokens.border}`,
                borderRadius: 6,
                overflow: 'hidden',
            }}>

                {/* ── Panel header strip ─────────────────────────────────── */}
                <div style={{
                    padding: '14px 24px',
                    background: tokens.surfaceHi,
                    borderBottom: `1px solid ${tokens.border}`,
                    display: 'flex', alignItems: 'center', gap: 12,
                }}>
                    <div style={{
                        width: 32, height: 32, borderRadius: 4,
                        background: tokens.sandDim,
                        border: `1px solid ${tokens.borderSand}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <Security style={{ color: tokens.sand, fontSize: 16 }} />
                    </div>
                    <div>
                        <div style={{
                            fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
                            color: tokens.textMuted, fontWeight: 600, lineHeight: 1.2,
                        }}>
                            Authorization Request
                        </div>
                        <div style={{ fontSize: 13, color: tokens.textPrimary, fontWeight: 600, marginTop: 2 }}>
                            {process.env.COMPANY_NAME || 'Nautilus Dynamics'} — Access Control
                        </div>
                    </div>
                </div>

                {/* ── Body ──────────────────────────────────────────────── */}
                <div style={{ padding: '20px 24px' }}>

                    {/* Client intro */}
                    <div style={{
                        padding: '12px 14px',
                        background: tokens.sandDim,
                        border: `1px solid ${tokens.borderSand}`,
                        borderLeft: `3px solid ${tokens.sand}`,
                        borderRadius: 4,
                        marginBottom: 20,
                    }}>
                        <span style={{ color: tokens.textSecondary, fontSize: 13 }}>
                            <span style={{ color: tokens.sand, fontWeight: 700 }}>
                                {consentData.client.client_name}
                            </span>
                            {' '}is requesting access to your {process.env.COMPANY_NAME || 'Nautilus Dynamics'} account.
                        </span>
                    </div>

                    {/* Permissions section label */}
                    <div style={{
                        fontSize: 10, letterSpacing: '0.10em', textTransform: 'uppercase',
                        color: tokens.textMuted, fontWeight: 700, marginBottom: 10,
                    }}>
                        Permissions Requested
                    </div>

                    {/* Scope rows */}
                    <div style={{
                        border: `1px solid ${tokens.border}`,
                        borderRadius: 4, overflow: 'hidden',
                        marginBottom: 24,
                    }}>
                        {consentData.scopes.map((scope, i) => {
                            const meta = scopeMeta[scope] ?? { ...defaultScopeMeta, label: scope.charAt(0).toUpperCase() + scope.slice(1) };
                            const { Icon } = meta;
                            return (
                                <div
                                    key={scope}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 14,
                                        padding: '11px 14px',
                                        borderBottom: i < consentData.scopes.length - 1 ? `1px solid ${tokens.border}` : 'none',
                                        background: tokens.surface,
                                    }}
                                >
                                    {/* Scope indicator */}
                                    <div style={{
                                        width: 28, height: 28, borderRadius: 4,
                                        background: tokens.surfaceHi,
                                        border: `1px solid ${tokens.border}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                    }}>
                                        <Icon style={{ color: tokens.sand, fontSize: 14 }} />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 13, color: tokens.textPrimary, fontWeight: 600 }}>
                                            {meta.label || scope.charAt(0).toUpperCase() + scope.slice(1)}
                                        </div>
                                        {meta.description && (
                                            <div style={{ fontSize: 11, color: tokens.textMuted, marginTop: 1 }}>
                                                {meta.description}
                                            </div>
                                        )}
                                    </div>
                                    {/* Granted indicator */}
                                    <Check style={{ color: tokens.sand, fontSize: 14, opacity: 0.6, flexShrink: 0 }} />
                                </div>
                            );
                        })}
                    </div>

                    {/* Divider */}
                    <div style={{ borderTop: `1px solid ${tokens.border}`, marginBottom: 20 }} />

                    {/* Fine print */}
                    <p style={{
                        fontSize: 11, color: tokens.textMuted, lineHeight: 1.5,
                        margin: '0 0 20px',
                    }}>
                        By authorizing, you grant <span style={{ color: tokens.textSecondary }}>{consentData.client.client_name}</span> the permissions listed above.
                        You can revoke access at any time from your account settings.
                    </p>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: 10 }}>
                        {/* Deny */}
                        <button
                            onClick={() => handleConsent(false)}
                            style={{
                                flex: 1,
                                padding: '10px 16px',
                                background: 'transparent',
                                border: `1px solid ${tokens.border}`,
                                borderRadius: 4,
                                color: tokens.textSecondary,
                                fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
                                fontWeight: 600, cursor: 'pointer',
                                transition: 'border-color 0.15s, color 0.15s',
                            }}
                            onMouseEnter={e => {
                                (e.currentTarget as HTMLButtonElement).style.borderColor = tokens.error;
                                (e.currentTarget as HTMLButtonElement).style.color = tokens.error;
                            }}
                            onMouseLeave={e => {
                                (e.currentTarget as HTMLButtonElement).style.borderColor = tokens.border;
                                (e.currentTarget as HTMLButtonElement).style.color = tokens.textSecondary;
                            }}
                        >
                            Deny
                        </button>

                        {/* Authorize */}
                        <button
                            onClick={() => handleConsent(true)}
                            style={{
                                flex: 1,
                                padding: '10px 16px',
                                background: tokens.sand,
                                border: `1px solid ${tokens.sand}`,
                                borderRadius: 4,
                                color: tokens.surface,
                                fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
                                fontWeight: 700, cursor: 'pointer',
                                transition: 'opacity 0.15s',
                            }}
                            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.88'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                        >
                            Authorize
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default OAuthConsentPage;
